/* eslint-disable max-lines */
import posthog from 'posthog-js';
import semver from 'semver';
import { Toast, TrackerCustomEvent } from 'common-package';
import { capLocation } from 'capacitor-plugin-location-coord';
import lookie from 'lookie';
import { klona } from 'klona';
// import axios from '@/service/common';
import service from '@/service';
import { ALL_PAGES, COORDINATE_TYPE, CURRENT_ANDROID_ID, STORAGE_LOCATION_COORDS } from '@/constant';

export * from './permission';
export * from './httpHandler';

export const isMobile = import.meta.env.VITE_CLIENT === 'mobile';

export { Toast };

export const getUrlParams = <T>(): T => {
  const url = window.location.href;
  const params = {};
  const searchParams = new URLSearchParams(url?.split('?')[1]);
  for (let [key, value] of searchParams) {
    params[key] = value;
  }
  return params as T;
};

export const getWelcomeText = () => {
  const hour = new Date().getHours();
  let welcomeText = '';

  if (hour >= 6 && hour <= 11) {
    welcomeText = '上午好';
  } else if (hour >= 12 && hour <= 17) {
    welcomeText = '下午好';
  } else {
    welcomeText = '晚上好';
  }
  return welcomeText;
};

export const step2Url: Record<Service.RealStep, string> = {
  ActionTest: ALL_PAGES.ACTION_TEST,
  MassageConfirming: ALL_PAGES.MASSAGE_SETTING,
  MassageSetting: ALL_PAGES.MASSAGE_SETTING,
  VisualCheck: ALL_PAGES.MASSAGE_CONTROL,
  Massage: ALL_PAGES.MASSAGE_CONTROL,
  Installation: ALL_PAGES.MASSAGE_CONTROL,
  VisualCheckAndPalpation: ALL_PAGES.MASSAGE_CONTROL,
  CustomPalpation: ALL_PAGES.MASSAGE_CONTROL,
  BodyMeasurement: ALL_PAGES.BODY_MEASUREMENT,
};

/** 通过订单信息跳转相关页面 */
export const handleNavFromOrderInfo = async (orderInfo: GetServiceOrderListResponse, nav, stepKey = 'step') => {
  if (!orderInfo?.[stepKey]) {
    Toast.show('当前订单已结束', {
      afterClose: () => nav(ALL_PAGES.MEMBER_DETAIL),
    });
    return;
  }

  if (orderInfo?.step === 'RoboticTask') {
    try {
      const currentStep = await service.lanInterface.getCurrentStep(orderInfo.deviceId);
      if (currentStep?.step) {
        nav(step2Url[currentStep?.step]);
      } else {
        nav(ALL_PAGES.MEMBER_DETAIL);
      }
    } catch {
      nav(ALL_PAGES.MEMBER_DETAIL);
    }
  } else {
    // 矩阵式艾灸设置
    if (orderInfo?.step === 'MassageSetting' && orderInfo.productItems?.includes('Moxibustion')) {
      nav(ALL_PAGES.MASSAGE_SETTING_MOX);
      return;
    }
    nav(step2Url[orderInfo[stepKey]]);
  }
};

/**
 * 阿里云上传文件OSS
 * @param file
 * @returns
 */

export const uploadFileCommon = async (file): Promise<string> => {
  if (!file) return;
  const genGenerateUrl = async file => {
    const timestamp = new Date().getTime();
    // 根据文件类型获取文件扩展名
    const getFileExtension = mimeType => {
      const mimeToExt = {
        // 图片类型
        'image/jpeg': '.jpeg',
        'image/jpg': '.jpg',
        'image/png': '.png',
        'image/gif': '.gif',
        'image/webp': '.webp',
        'image/svg+xml': '.svg',
        'image/bmp': '.bmp',
        'image/tiff': '.tiff',
        // 文档类型
        'application/pdf': '.pdf',
        'application/msword': '.doc',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
        'application/vnd.ms-excel': '.xls',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
        'application/vnd.ms-powerpoint': '.ppt',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
        'text/plain': '.txt',
        'text/csv': '.csv',
        'text/html': '.html',

        // 压缩文件
        'application/zip': '.zip',
        'application/x-rar-compressed': '.rar',
        'application/x-7z-compressed': '.7z',
        'application/gzip': '.gz',

        // 音频视频
        'audio/mpeg': '.mp3',
        'audio/wav': '.wav',
        'audio/ogg': '.ogg',
        'video/mp4': '.mp4',
        'video/avi': '.avi',
        'video/mpeg': '.mpeg',
        'video/quicktime': '.mov',
        'video/x-msvideo': '.avi',

        // 其他常见类型
        'application/json': '.json',
        'application/xml': '.xml',
        'application/javascript': '.js',
        'text/css': '.css',
      };

      return mimeToExt[mimeType] || '.bin'; // 如果没有匹配到，使用默认的 .bin 扩展名
    };

    // 从原始文件名中提取基本名称（去掉已有扩展名）
    const getBaseFileName = originalName => {
      if (!originalName) return 'file';

      const lastDotIndex = originalName.lastIndexOf('.');
      if (lastDotIndex === -1) return originalName;

      return originalName.substring(0, lastDotIndex);
    };

    let fileName;
    if (file.name) {
      const baseName = getBaseFileName(file.name);
      const extension = getFileExtension(file.type || file.mimeType);
      fileName = `${baseName}_${timestamp}${extension}`;
    } else {
      const extension = getFileExtension(file.type);
      fileName = `file_${timestamp}${extension}`;
    }

    const { data } = await service.upload.preUpload(fileName);
    const { generateUrl } = data;
    // 写入文件
    await fetch(generateUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': 'application/octet-stream',
      },
    });

    return {
      downloadUrl: data?.downloadUrl, //下载地址
    };
  };
  return new Promise(async (resolve, reject) => {
    try {
      const { downloadUrl } = await genGenerateUrl(file);
      resolve(downloadUrl);
    } catch (e) {
      reject(e.toString());
    }
  });
};

export const base64StringtoFile = (
  base64String: string,
  filename: string = 'image.jpg',
  type: string = 'image/jpeg'
) => {
  let bstr = window.atob(base64String?.split(',').pop());
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  bstr = null;
  return new File([u8arr], filename, { type });
};

export const bytesToMB = (bytes: number) => {
  if (!bytes) return 0;
  const MB = bytes / (1024 * 1024);
  return +MB.toFixed(3);
};

export const formatSeconds = (seconds: number): string => {
  if (isNaN(seconds) || seconds < 0) {
    return '';
  }
  const min = Math.floor(seconds / 60);
  const sec = Math.floor(seconds % 60);
  // 使用padStart函数将数字前面补0，保证两位数
  return min.toString() + '分' + sec.toString().padStart(2, '0') + '秒';
};

const getFilenameFromResponse = response => {
  const contentDisposition = response.headers.get('content-disposition');
  if (!contentDisposition) return null;

  // 从 Content-Disposition 头提取文件名
  const matches = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
  if (matches && matches[1]) {
    // 移除可能的引号
    return matches[1].replace(/['"]/g, '');
  }
  return null;
};

export const uploadBase64Img = (paylod: string, isBlobUrl = false): Promise<string> => {
  if (!paylod) return;
  return new Promise(async (resolve, reject) => {
    let formData = new FormData();
    let arr: string[],
      u8arr: Uint8Array,
      file: File,
      mime = 'image/png',
      blob: Blob = null,
      newFile: File;

    if (isBlobUrl) {
      const response = await fetch(paylod);
      blob = await response.blob();
      // 从响应头获取文件名，或使用默认文件名
      const filename = getFilenameFromResponse(response) || 'file.txt';
      // 或从响应头获取 MIME 类型，或使用 blob 的类型
      const mimeType = response.headers.get('content-type') || blob.type;
      // 将 Blob 转换为 File
      const file = new File([blob], filename, {
        type: mimeType,
        lastModified: Date.now(),
      });
      newFile = file;
    } else {
      arr = paylod?.split(',');
      mime = arr[0].match(/:(.*?);/)[1];
      let bstr = window.atob(arr[1]);
      let n = bstr.length;
      u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      //@ts-ignore
      let file = new File([u8arr], 'screenshot.png', { type: mime });
      newFile = file;
    }

    try {
      const downloadUrl = await uploadFileCommon(newFile);
      resolve(downloadUrl);
    } catch (e) {
      reject(e.toString());
    } finally {
      arr = null;
      u8arr = null;
      file = null;
      blob = null;
      formData = null;
    }
  });
};

export const base64String2ObjectURL = (base64String: string) => {
  if (!base64String) return;
  return URL.createObjectURL(base64StringtoFile(base64String));
};

export const sentryTrackEnv: ENVType[] = ['qa', 'pre', 'prod'];

export const transformVipName2Text = (userVipName: string) => {
  if (!userVipName) return;

  return userVipName?.includes('银')
    ? 'silver'
    : userVipName?.includes('金')
    ? 'gold'
    : userVipName?.includes('黑')
    ? 'black'
    : null;
};

/**
 * 判断机器人软件版本是否是新版本
 * @param version
 * @param compareVersion
 * @returns Boolean
 * @default 1.5.5
 */
export const isNewSoftwareVersion = (version: string, compareVersion = '1.5.5') => {
  if (!version) return false;
  return semver.gte(version, compareVersion);
};

export const getServerUrlFromConnection = (serverInfo: GetConnectionInfoResponse) =>
  `https://${serverInfo?.host}:${serverInfo.port}`;

/**
 * 将对象解析为路由query-string
 * @param params
 * @returns
 */
export const formatParams2QueryString = (params: { [key: string]: any }) => {
  if (!params || !Object.keys(params)?.length) return '';
  return Object.entries(params).reduce(
    (accur, [key, val], index) =>
      accur + `${val === null || val === undefined ? '' : `${index === 0 ? '' : '&'}${key}=${val}`}`,
    '?'
  );
};

/**
 * 遮挡手机号码
 * @param phoneNumber
 * @returns
 */
export const maskPhoneNumber = (phoneNumber: string) => {
  const start = phoneNumber.slice(0, 3);
  const end = phoneNumber.slice(-4);
  const middle = phoneNumber.slice(3, -4).replace(/\d/g, '*');
  return start + middle + end;
};

/**
 * 遮挡手机号码（始终保留后四位，其余部分替换为*）
 * @param phoneNumber 输入的手机号字符串（支持国内外手机号、含/不含国际区号）
 * @returns 遮挡后的手机号
 */
export const maskPhoneNumberInterNational = (phoneNumber: string) => {
  // 若手机号长度≤4，直接返回原字符串（无需要遮挡的部分）
  if (phoneNumber?.length <= 4) {
    return phoneNumber;
  }
  // 截取后四位（固定保留）
  const end = phoneNumber?.slice(-4);
  // 截取前n位（除后四位外的所有字符），统一替换为*
  const mask = phoneNumber?.slice(0, -4).replace(/./g, '*');
  return mask + end;
};

/**
 * 通过生日计算年龄
 */
export const getAgeFromBirthday = (dateStr: string): number | '' => {
  if (!dateStr) return '';

  const birthday = new Date(dateStr);
  const ageDifMs = Date.now() - birthday.getTime();
  const ageDate = new Date(ageDifMs);
  return Math.abs(ageDate.getUTCFullYear() - 1970);
};

// 年龄显示统一规则处理
export const showBirthday = (age: number, birthday: string) => {
  return birthday ? `${getAgeFromBirthday(birthday)}岁` : `${age}岁`;
};

/**
 * 随机获取数组中的某项，并将其从数组中移除
 */
export class RandomPicker<T extends any[]> {
  private items = null;
  private backupProps = null;

  constructor(props: T) {
    this.items = klona(props);
    this.backupProps = props;
  }

  getRandomItem(): T[0] {
    if (!this.items) return;

    if (this.items?.length === 0) {
      /** 当前数组为空，重置数组 */
      this.items = klona(this.backupProps);
    }
    let randomIndex = Math.floor(Math.random() * this.items?.length);
    let randomItem = this.items?.[randomIndex];

    this.items?.splice(randomIndex, 1);

    return randomItem;
  }
}

/**
 * 将数组顺序打乱
 */
export const shuffleArray = <T extends any[]>(array: T): T => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
};

/**
 * 将数组补齐到指定长度
 */
export const padArrayToLength = <T extends any[]>(arr: T, padLength: number, fillValue = null): T => {
  if (!(arr && padLength)) return [] as T;
  const newArr = klona(arr);
  // 如果数组长度小于8，则用null填充
  while (newArr.length < padLength) {
    newArr.push(fillValue);
  }
  // 如果数组长度大于8，则不进行操作
  return newArr;
};

export const delay = (ms: number) => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * 用于Echarts图尺寸大小
 */
export const px2vw = (size: number) => {
  const clientWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
  if (!clientWidth) return;
  let fontSize = clientWidth / 1300;
  return size * fontSize;
};

/**
 * 根据开始时间 结束时间生成日期数组
 * @param startTime 开始时间
 * @param endTime 结束时间
 * @returns {string[]} 日期数组
 */

export const generateDateRange = (startTime: number, endTime: number) => {
  // 先判断一下 那个是开始时间 哪个是结束时间
  let dateArray = [];
  let currentDate = new Date(startTime);
  const end = new Date(endTime);

  while (currentDate <= end) {
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const day = String(currentDate.getDate()).padStart(2, '0');
    dateArray.push(`${year}/${month}/${day}`);
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return dateArray;
};

export const getLocationCoord = (
  appVersion: string
): Promise<{
  lat: number;
  lng: number;
  coordinateType: CreateServiceOrderV3Params['coordinateType'];
}> => {
  return new Promise((resolve, reject) => {
    // 版本号大于等于 v1.11.0 使用百度定位SDK
    if (isNewSoftwareVersion(appVersion, '1.11.0')) {
      if (isNewSoftwareVersion(appVersion, '1.13.0')) {
        const storageCoords = lookie.get(STORAGE_LOCATION_COORDS);
        if (
          storageCoords?.lat &&
          storageCoords?.lng &&
          storageCoords?.timestamp &&
          Date.now() - storageCoords?.timestamp < 10 * 60 * 1000
        ) {
          // 有本地缓存且缓存获取时间小于10分钟，取本地缓存
          resolve({
            lat: storageCoords.lat,
            lng: storageCoords.lng,
            coordinateType: storageCoords?.coordType || COORDINATE_TYPE.WGS84,
          });
          return;
        }

        const timeoutControl = () =>
          new Promise((_, reject) => {
            setTimeout(() => reject(new Error('CapLocation timeout')), 2000);
          });

        Promise.race([
          capLocation.getCoords(),
          timeoutControl(), // 2秒超时限制
        ])
          .then(wifiLocInfo => {
            console.log('WIFI和基站获取的定位信息', wifiLocInfo);

            lookie.set(STORAGE_LOCATION_COORDS, {
              //@ts-ignore
              lat: +wifiLocInfo.lat?.toFixed?.(6),
              //@ts-ignore
              lng: +wifiLocInfo.lng?.toFixed?.(6),
              timestamp: Date.now(),
              coordType: COORDINATE_TYPE.WGS84,
            });

            resolve({
              //@ts-ignore
              lat: +wifiLocInfo.lat?.toFixed?.(6),
              //@ts-ignore
              lng: +wifiLocInfo.lng?.toFixed?.(6),
              coordinateType: COORDINATE_TYPE.WGS84,
            });
          })
          .catch(e => {
            // 百度地图SDK
            console.log('百度地图SDK');
            const timer = setTimeout(() => {
              console.log('未获取到定位信息，走异常兜底逻辑，尝试取本地缓存');
              if (storageCoords) {
                resolve({
                  lat: storageCoords?.lat,
                  lng: storageCoords?.lng,
                  coordinateType: storageCoords?.coordType || COORDINATE_TYPE.WGS84,
                });
              } else {
                resolve({
                  lat: null,
                  lng: null,
                  coordinateType: null,
                });
              }
            }, 8000);
            window.baidumap_location.getCurrentPosition(
              result => {
                console.log('baidu location', result, JSON.stringify(result, null, 4));

                clearTimeout(timer);
                lookie.set(STORAGE_LOCATION_COORDS, {
                  lat: result.latitude,
                  lng: result.longitude,
                  timestamp: Date.now(),
                  coordType: COORDINATE_TYPE.BAIDU,
                });

                /** 记录百度获取定位成功而WIFI和基站获取失败的情况 */
                posthog.capture(TrackerCustomEvent.GetWifiLocError, {
                  et: 'monitor',
                  c1: e?.toString?.(),
                });

                resolve({
                  lat: result?.latitude,
                  lng: result?.longitude,
                  coordinateType: COORDINATE_TYPE.BAIDU,
                });
              },
              error => {
                console.log('【baidumap_location】getCurrentPosition error::', error);

                clearTimeout(timer);
                if (storageCoords) {
                  resolve({
                    lat: storageCoords?.lat,
                    lng: storageCoords?.lng,
                    coordinateType: storageCoords?.coordType || COORDINATE_TYPE.WGS84,
                  });
                } else {
                  resolve({
                    lat: null,
                    lng: null,
                    coordinateType: null,
                  });
                }

                posthog.capture(TrackerCustomEvent.DeviceGPSAbnormal, {
                  et: 'monitor',
                  c1: lookie.get(CURRENT_ANDROID_ID),
                  c2: JSON.stringify(error),
                });
              }
            );
          });
      } else {
        console.log('旧版本APP不兼容，已设置强制更新');
        resolve({
          lat: null,
          lng: null,
          coordinateType: null,
        });
      }
    } else {
      // 高德地图SDK
      window.AmapLocation?.getCurrentPosition(
        result => {
          console.log('获取的位置信息:', result);

          if (result.status === 'success') {
            resolve({
              lat: result?.latitude,
              lng: result?.longitude,
              coordinateType: COORDINATE_TYPE.GAODE,
            });
          } else {
            reject(result);
          }
        },
        error => {
          console.error('AmapLocation.getCurrentPosition Error', error);
          reject(error);
          posthog.capture(TrackerCustomEvent.DeviceGPSAbnormal, {
            et: 'monitor',
            c1: lookie.get(CURRENT_ANDROID_ID),
            c2: JSON.stringify(error),
          });
        }
      );
    }
  });
};

/**
 * 将数字转成大写形式
 * 支持100以内的数字
 */
export const numberToChinese = (number: number, characters?: string[]) => {
  if (!number) return '';

  if (number < 10) {
    return (characters || ['零', '一', '二', '三', '四', '五', '六', '七', '八', '九'])[number];
  } else if (number < 100) {
    const tenUnit = Math.floor(number / 10);
    const unit = number % 10;
    return numberToChinese(tenUnit) + '十' + (unit === 0 ? '' : numberToChinese(unit));
  } else {
    return '';
  }
};

/** px转vw */
export const pxToVw = (px: number, viewportWidth: number) => {
  return (px / viewportWidth) * 100 + 'vw';
};
